//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { useEffect, useState } from 'react';

import { isDefined } from '@utils/filterPredicates';

export type PostMessageFilter<T> = (message: T) => T | undefined;
export type PostMessageProcessor<T> = (event?: MessageEvent<T>) => void;
export type PostMessageListener<T> = (
  message: T,
  event: MessageEvent<T>,
) => void;

export interface PostMessageHandler<T> {
  processor: PostMessageProcessor<T>;
}

export interface PostMessageListenerParams<T> {
  filter: PostMessageFilter<T>;
  listener: PostMessageListener<T>;
  origin?: string;
  target?: Window;
}

/**
 * A custom hook that listens for post messages from a specified target.
 *
 * This hook sets up an event listener for the 'message' event and applies a filter to the
 * incoming messages. If a message passes the filter, a listener is invoked with the filtered message.
 *
 * @template T - The type of the message data being handled.
 *
 * @param {PostMessageListenerParams<T>} params - The parameters for setting up the post message listener.
 * @param {PostMessageFilter<T>} params.filter - A function that filters messages.
 * It takes a message of type T and returns either the message (if it should be processed) or undefined (if it should be ignored).
 * @param {PostMessageListener<T>} params.listener - A function that is called with the filtered message and original event when a valid message is received.
 * @param {string} [params.origin=window.location.origin] - The expected origin of the incoming messages. Defaults to the current origin.
 * @param {Window} [params.target=window] - The target window to listen for messages from. Defaults to the current window.
 *
 */
export const usePostMessageListener = <T>({
  filter,
  listener,
  origin = window.location.origin,
  target = window,
}: PostMessageListenerParams<T>) => {
  const [handler] = useState<PostMessageHandler<T>>({
    processor: (event?: MessageEvent<T>) => {
      if (isDefined(event)) {
        if (origin !== '*' && event.origin !== origin) {
          return;
        }
        const filtered = filter(event.data);
        if (typeof filtered !== 'undefined') {
          listener(filtered, event);
          target.removeEventListener('message', handler.processor);
        }
      }
    },
  });

  useEffect(() => {
    target.addEventListener('message', handler.processor);

    return () => {
      target.removeEventListener('message', handler.processor);
    };
  }, [handler, target]);
};
